// src/utils/index.js
// Fonctions utilitaires
import { format } from 'date-fns';
import { fr as frLocale, enUS } from 'date-fns/locale';
import i18n from '../i18n';

/**
 * Calcule l'âge à partir d'une date de naissance.
 * Accepte un Firestore Timestamp (.toDate()), un objet Date, un nombre (epoch ms), ou une string ISO.
 * Retourne null si la date est invalide.
 */
export const calculateAge = (dateOfBirth) => {
  if (!dateOfBirth) return null;

  let date;
  if (dateOfBirth.toDate) {
    date = dateOfBirth.toDate();
  } else if (dateOfBirth instanceof Date) {
    date = dateOfBirth;
  } else if (typeof dateOfBirth === 'number' || typeof dateOfBirth === 'string') {
    date = new Date(dateOfBirth);
  } else {
    return null;
  }

  if (isNaN(date.getTime())) return null;

  const today = new Date();
  let age = today.getFullYear() - date.getFullYear();
  const m = today.getMonth() - date.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < date.getDate())) {
    age--;
  }
  return age;
};

/**
 * Retourne l'âge affichable d'un profil.
 * Priorité : dateOfBirth (calculé) > age (champ legacy).
 */
export const getDisplayAge = (profile) => {
  if (!profile) return null;
  const computed = calculateAge(profile.dateOfBirth);
  if (computed !== null) return computed;
  return profile.age ?? null;
};

/**
 * Retourne le genre normalisé d'un profil.
 * Mappe "O" (legacy) vers "NB" pour compatibilité avec les nouveaux filtres/affichage.
 */
export const getDisplayGender = (profile) => {
  if (!profile) return null;
  const g = profile.gender;
  if (g === 'O') return 'NB';
  return g || null;
};

/**
 * Retourne le premier prompt/answer pour affichage court (cards).
 */
export const getFirstBioPrompt = (profile) => {
  if (!profile) return null;
  if (profile.bioPrompts && profile.bioPrompts.length > 0) {
    return profile.bioPrompts[0];
  }
  if (profile.bio) {
    return { prompt: 'constants.bioPrompts.about', answer: profile.bio };
  }
  return null;
};

/**
 * Valide un mot de passe et retourne sa force + erreurs.
 * Strength 0-4 : très faible, faible, moyen, fort, très fort.
 */
export const validatePassword = (password) => {
  const errors = [];
  if (!password || password.length < 8) {
    errors.push('Le mot de passe doit faire au moins 8 caractères');
  }

  let strength = 0;
  if (password && password.length >= 8) strength++;
  if (password && password.length >= 12) strength++;
  if (/[A-Z]/.test(password)) strength++;
  if (/[0-9]/.test(password)) strength++;
  if (/[^A-Za-z0-9]/.test(password)) strength++;

  const labelKeys = [
    'constants.passwordStrength.veryWeak',
    'constants.passwordStrength.weak',
    'constants.passwordStrength.medium',
    'constants.passwordStrength.strong',
    'constants.passwordStrength.veryStrong',
  ];
  const labelKey = password ? labelKeys[Math.min(strength, 4)] : '';

  return { strength, labelKey, errors };
};

/**
 * Valide une date de naissance.
 * Retourne { valid, age, error } — min 15 ans, max 120 ans.
 */
export const validateDateOfBirth = (dateString) => {
  if (!dateString)
    return { valid: false, age: null, error: 'La date de naissance est obligatoire' };

  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    return { valid: false, age: null, error: 'Date de naissance invalide' };
  }

  const age = calculateAge(date);
  if (age === null) return { valid: false, age: null, error: 'Date de naissance invalide' };
  if (age < 15)
    return { valid: false, age, error: 'Vous devez avoir au moins 15 ans pour vous inscrire.' };
  if (age > 120) return { valid: false, age, error: 'Date de naissance invalide' };

  return { valid: true, age, error: null };
};

/**
 * Vérifie si un profil a complété l'onboarding.
 * Traite undefined comme true (compatibilité users existants).
 */
export const isProfileOnboardingComplete = (user) => {
  if (!user) return false;
  if (user.onboardingComplete === undefined) return true;
  return user.onboardingComplete === true;
};

/**
 * Vérifie si un utilisateur peut accéder à l'espace Rencontres.
 * Requiert mode dating/both OU datingEnabled legacy, ET âge >= 18.
 */
export const canAccessDating = (user) => {
  if (!user) return false;
  const hasDatingMode =
    user.mode === 'dating' || user.mode === 'both' || user.datingEnabled === true;
  if (!hasDatingMode) return false;
  const age = calculateAge(user.dateOfBirth);
  return age !== null && age >= 18;
};

/**
 * Calcule la distance en km entre deux utilisateurs (formule Haversine).
 * Retourne null si les coordonnées sont manquantes.
 */
export const getDistance = (user, profile) => {
  if (!user || !profile) return null;
  const lat1 = user.cityLat;
  const lng1 = user.cityLng;
  const lat2 = profile.cityLat;
  const lng2 = profile.cityLng;
  if (lat1 === null || lat1 === undefined) return null;
  if (lng1 === null || lng1 === undefined) return null;
  if (lat2 === null || lat2 === undefined) return null;
  if (lng2 === null || lng2 === undefined) return null;

  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return Math.round(R * c);
};

/**
 * Retourne les centres d'intérêt en commun entre deux utilisateurs.
 * Chaque élément : { value, label }. Retourne [] si aucun.
 */
export const getCommonInterests = (user, profile, interestsList) => {
  if (!user?.interests?.length || !profile?.interests?.length || !interestsList) return [];
  const set = new Set(user.interests);
  return profile.interests
    .filter((v) => set.has(v))
    .map((v) => {
      const found = interestsList.find((i) => i.value === v);
      return { value: v, labelKey: found ? found.labelKey : null };
    });
};

/**
 * Calcule le score de compatibilité entre deux profils dating.
 * Retourne { score: 0-100, details: { age, intention, distance, interests, education } }
 */
export const calculateCompatibility = (currentUser, targetProfile) => {
  if (!currentUser || !targetProfile) return { score: 0, details: {} };

  const curPrefs = currentUser.datingPreferences || {};
  const tarPrefs = targetProfile.datingPreferences || {};
  const details = { age: 0, intention: 0, distance: 0, interests: 0, education: 0 };

  // ── Âge mutuel (+30) ──
  const curAge = calculateAge(currentUser.dateOfBirth);
  const tarAge = calculateAge(targetProfile.dateOfBirth);
  const curAgeRange = curPrefs.ageRange;
  const tarAgeRange = tarPrefs.ageRange;

  const curInRange =
    tarAge !== null && curAgeRange && curAgeRange.length === 2
      ? tarAge >= curAgeRange[0] && tarAge <= curAgeRange[1]
      : true; // pas de préférence = accepte tout
  const tarInRange =
    curAge !== null && tarAgeRange && tarAgeRange.length === 2
      ? curAge >= tarAgeRange[0] && curAge <= tarAgeRange[1]
      : true;

  if (curInRange && tarInRange) details.age = 30;
  else if (curInRange || tarInRange) details.age = 15;

  // ── Même intention (+25) ──
  const curInt = currentUser.intention;
  const tarInt = targetProfile.intention;
  if (curInt && tarInt) {
    if (curInt === tarInt) details.intention = 25;
    else if (curInt === 'open' || tarInt === 'open') details.intention = 15;
  }

  // ── Distance mutuelle (+20) ──
  const dist = getDistance(currentUser, targetProfile);
  if (dist === null) {
    // coordonnées manquantes → neutre
    details.distance = 10;
  } else {
    const curMaxDist = curPrefs.maxDistance;
    const tarMaxDist = tarPrefs.maxDistance;
    const curOk = curMaxDist === null || curMaxDist === undefined ? true : dist <= curMaxDist;
    const tarOk = tarMaxDist === null || tarMaxDist === undefined ? true : dist <= tarMaxDist;

    if (curOk && tarOk) details.distance = 20;
    else if (curOk || tarOk) details.distance = 10;
  }

  // ── Intérêts communs (+15) ──
  const curI = currentUser.interests || [];
  const tarI = targetProfile.interests || [];
  const curSet = new Set(curI);
  const cCount = tarI.filter((v) => curSet.has(v)).length;
  if (cCount >= 5) details.interests = 15;
  else if (cCount >= 3) details.interests = 10;
  else if (cCount >= 1) details.interests = 5;

  // ── Même études (+10) ──
  const curEdu = currentUser.education;
  const tarEdu = targetProfile.education;
  if (curEdu && tarEdu) {
    if (curEdu === tarEdu) details.education = 10;
  } else if (curEdu || tarEdu) {
    details.education = 5;
  }

  let total =
    details.age + details.intention + details.distance + details.interests + details.education;

  // Pénalité profil incomplet
  if (profileCompletion(targetProfile) < 50) {
    total = Math.round(total * 0.7);
  }

  return { score: Math.max(0, Math.min(100, Math.round(total))), details };
};

const getDateLocale = () => (i18n.language?.startsWith('en') ? enUS : frLocale);

/**
 * Formate un timestamp Firestore en temps relatif.
 * compact=true : format court sans "il y a" / "ago" (pour ConversationItem).
 * Utilise i18n pour les clés dates.*.
 */
export const timeAgo = (timestamp, { compact = false } = {}) => {
  if (!timestamp) return '';
  const date = timestamp.toDate
    ? timestamp.toDate()
    : timestamp.seconds
      ? new Date(timestamp.seconds * 1000)
      : new Date(timestamp);
  if (isNaN(date.getTime())) return '';
  const t = i18n.t.bind(i18n);
  const diff = Math.floor((Date.now() - date.getTime()) / 1000);
  if (diff < 60) return t('dates.justNow');
  if (diff < 3600) {
    const m = Math.floor(diff / 60);
    return compact ? t('dates.minutesCompact', { count: m }) : t('dates.minutesAgo', { count: m });
  }
  if (diff < 86400) {
    const h = Math.floor(diff / 3600);
    return compact ? t('dates.hoursCompact', { count: h }) : t('dates.hoursAgo', { count: h });
  }
  if (diff < 604800) {
    const d = Math.floor(diff / 86400);
    return compact ? t('dates.daysCompact', { count: d }) : t('dates.daysAgo', { count: d });
  }
  return format(date, 'd MMM', { locale: getDateLocale() });
};

/**
 * Formate un timestamp Firestore en date complète, localisée.
 * FR : "samedi 15 mars 2026 à 14h30" / EN : "Saturday, March 15, 2026 at 2:30 PM"
 * shortWeekday=true : "sam." / "Sat"
 */
export const formatDate = (ts, { shortWeekday = false } = {}) => {
  if (!ts) return '';
  const d = ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts);
  if (isNaN(d.getTime())) return '';
  const locale = getDateLocale();
  const isEn = i18n.language?.startsWith('en');
  if (isEn) {
    const pattern = shortWeekday
      ? "EEE, MMMM d, yyyy 'at' h:mm a"
      : "EEEE, MMMM d, yyyy 'at' h:mm a";
    return format(d, pattern, { locale });
  }
  const pattern = shortWeekday ? "EEE d MMMM yyyy 'à' HH'h'mm" : "EEEE d MMMM yyyy 'à' HH'h'mm";
  return format(d, pattern, { locale });
};

/**
 * Formate un timestamp en date courte localisée (sans heure ni année).
 * FR : "sam. 15 mars" / EN : "Sat, March 15"
 */
export const formatDateShort = (ts) => {
  if (!ts) return '';
  const d = ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts);
  if (isNaN(d.getTime())) return '';
  const locale = getDateLocale();
  const isEn = i18n.language?.startsWith('en');
  return format(d, isEn ? 'EEE, MMMM d' : 'EEE d MMMM', { locale });
};

/**
 * Formate un prix en euros. Retourne "Gratuit"/"Free" si 0 ou falsy.
 */
export const formatPrice = (price) => {
  if (!price || price === 0) return i18n.t('common.free');
  return `${price} €`;
};

/**
 * Calcule le % de complétion du profil utilisateur.
 * Critères adaptés selon le mode :
 *   community (ou absent) : photo, bio, intérêts
 *   dating / both : photo, bio, intérêts, lookingFor, intention
 */
export const profileCompletion = (user) => {
  if (!user) return 0;

  const hasPhoto = user.profilePicture && !user.profilePicture.includes('dicebear');
  const hasBio = (user.bioPrompts && user.bioPrompts.length > 0) || !!user.bio;
  const hasInterests = user.interests && user.interests.length >= 3;

  const mode = user.mode || 'community';
  if (mode === 'dating' || mode === 'both') {
    const hasLookingFor = !!user.lookingFor;
    const hasIntention = !!user.intention;
    const hasHeight = user.height !== null && user.height !== undefined;
    const hasEducation = user.education !== null && user.education !== undefined;
    const hasProfession = !!user.profession;
    const filled = [
      hasPhoto,
      hasBio,
      hasInterests,
      hasLookingFor,
      hasIntention,
      hasHeight,
      hasEducation,
      hasProfession,
    ].filter(Boolean).length;
    return Math.round((filled / 8) * 100);
  }

  const filled = [hasPhoto, hasBio, hasInterests].filter(Boolean).length;
  return Math.round((filled / 3) * 100);
};

/**
 * Retourne la liste des items manquants pour la complétion du profil.
 * Clés i18n retournées (pas de texte hardcodé).
 */
export const getProfileMissingItems = (user) => {
  if (!user) return [];
  const missing = [];
  if (!user.profilePicture || user.profilePicture.includes('dicebear'))
    missing.push('completion.photo');
  if ((!user.bioPrompts || user.bioPrompts.length === 0) && !user.bio)
    missing.push('completion.bio');
  if (!user.interests || user.interests.length < 3) missing.push('completion.interests');

  const mode = user.mode || 'community';
  if (mode === 'dating' || mode === 'both') {
    if (!user.lookingFor) missing.push('completion.lookingFor');
    if (!user.intention) missing.push('completion.intention');
    if (user.height === null || user.height === undefined) missing.push('completion.height');
    if (user.education === null || user.education === undefined)
      missing.push('completion.education');
    if (!user.profession) missing.push('completion.profession');
  }
  return missing;
};
